import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SpecialResponseRenderer, { hasSpecialResponseMarkers } from '../../../components/Assistant/SpecialResponseRenderer';
import { processMessageText } from '../utils/chatMessageUtils';

const ChatMessageContent = ({ message, onSourceClick }) => {
  const processedText = processMessageText(message.text);

  if (hasSpecialResponseMarkers(processedText)) {
    return <SpecialResponseRenderer content={processedText} />;
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h3: ({ children, ...props }) => (
          <h3 className="text-lg font-bold mt-4 mb-2" {...props}>{children}</h3>
        ),
        strong: ({ ...props }) => <strong className="font-bold" {...props} />,
        em: ({ ...props }) => <em className="italic" {...props} />,
        ul: ({ ...props }) => <ul className="list-disc list-outside my-3 pl-6 space-y-2" {...props} />,
        ol: ({ ...props }) => <ol className="list-decimal list-outside my-3 pl-6 space-y-2" {...props} />,
        li: ({ ...props }) => <li className="pl-2" {...props} />,
        table: ({ ...props }) => (
          <div className="overflow-x-auto my-4 w-full" style={{ display: 'block' }}>
            <table
              className="min-w-full table-auto border-collapse border border-gray-300 bg-white rounded-lg shadow-sm"
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                backgroundColor: 'white',
                border: '1px solid #d1d5db'
              }}
              {...props}
            />
          </div>
        ),
        thead: ({ ...props }) => <thead className="bg-gray-50" {...props} />,
        tbody: ({ ...props }) => <tbody {...props} />,
        tr: ({ ...props }) => (
          <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150" {...props} />
        ),
        th: ({ ...props }) => (
          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border border-gray-300 bg-gray-100" {...props} />
        ),
        td: ({ ...props }) => (
          <td className="px-4 py-3 text-sm text-gray-700 border border-gray-300 align-top" {...props} />
        ),
        a: ({ children, ...props }) => (
          <a
            className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors duration-200"
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          >
            {children}
          </a>
        ),
        code: ({ ...props }) => (
          <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono" {...props} />
        ),
        pre: ({ ...props }) => (
          <pre className="bg-gray-100 p-4 rounded text-sm font-mono overflow-x-auto" {...props} />
        ),
        p: ({ children, ...props }) => {
          const mappedChildren = React.Children.map(children, (child) => {
            if (typeof child !== 'string') return child;

            return child.split(/(【\d+:\d+†[^】]+】)/g).map((part, index) => {
              if (!part.match(/【\d+:\d+†[^】]+】/)) return part;

              const source = part.slice(1, -1);
              const citation = message.annotations?.find((annotation) =>
                annotation.file_citation?.quote && annotation.text === part
              )?.file_citation?.quote || '';

              return (
                <span
                  key={index}
                  className="text-primary-500 cursor-pointer hover:underline ml-2"
                  onClick={() => onSourceClick({ source, citation })}
                >
                  Source
                </span>
              );
            });
          });

          return <p {...props}>{mappedChildren}</p>;
        }
      }}
    >
      {processedText}
    </ReactMarkdown>
  );
};

export default ChatMessageContent;
